import { db } from './index.js';
import type { TabSong } from '../types/index.js';
import { LIBRARY_CHANGED_EVENT } from '../sync/events.js';

let claimPromise: Promise<void> | null = null;

/**
 * FRE-142 D5: when a guest session signs in or registers, the guest-scope rows created during that session
 * (tabs, playlists, favorites, last-opened tab) are re-owned by the new account. Never throws.
 * `guestStartedAt` is null for guest sessions that predate this release: then every guest-scope row is claimed.
 */
export function startGuestClaim(uid: string, guestStartedAt: string | null): Promise<void> {
  const run = claimGuestLibrary(uid, guestStartedAt)
    .then(() => {
      if (typeof window !== 'undefined') window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
    })
    .catch((err) => {
      console.error('Guest library claim failed:', err);
    })
    .finally(() => {
      if (claimPromise === run) claimPromise = null;
    });
  claimPromise = run;
  return run;
}

/** Resolves immediately when no claim is running. */
export function waitForGuestClaim(): Promise<void> {
  return claimPromise ?? Promise.resolve();
}

async function claimGuestLibrary(uid: string, since: string | null): Promise<void> {
  const inScope = (iso: string | undefined): boolean => !since || (iso ?? '') >= since;
  await db.transaction('rw', [db.tabs, db.playlists, db.userFavorites, db.settings, db.lyricSheets], async () => {
    const guestTabs = (await db.tabs.toArray()).filter((t) => !t.ownerId);
    const guestPlaylists = (await db.playlists.toArray()).filter((p) => !p.ownerId && inScope(p.createdAt));
    const guestFavs = (await db.userFavorites.where('ownerId').equals('').toArray()).filter((f) =>
      inScope(f.addedAt)
    );

    // A guest tab that a claimed favorite / playlist points at must come along, even when it was downloaded
    // (created on the server) before the guest session started; otherwise the reference would dangle.
    const referenced = new Set<string>([
      ...guestFavs.map((f) => f.tabId),
      ...guestPlaylists.flatMap((p) => p.tabIds)
    ]);

    for (const t of guestTabs) {
      if (t.removedAt) {
        await db.tabs.delete(t.id);
        continue;
      }
      if (!inScope(t.createdAt) && !referenced.has(t.id)) continue;
      const next: TabSong = { ...t, ownerId: uid };
      if (t.pendingUpload === 1) next.uploaderId = uid;
      await db.tabs.put(next);
    }

    for (const p of guestPlaylists) {
      await db.playlists.put({ ...p, ownerId: uid, dirty: 1, updatedAt: new Date().toISOString() });
    }

    for (const l of (await db.lyricSheets.toArray()).filter((r) => !r.ownerId && inScope(r.createdAt))) {
      if (l.deletedAt) await db.lyricSheets.delete(l.id);
      else await db.lyricSheets.put({ ...l, ownerId: uid, dirty: 1, updatedAt: new Date().toISOString() });
    }

    for (const f of guestFavs) {
      await db.userFavorites.delete(['', f.tabId]);
      if (!(await db.userFavorites.get([uid, f.tabId]))) {
        await db.userFavorites.put({ ...f, ownerId: uid, dirty: 1, serverKnown: 0, updatedAt: new Date().toISOString() });
      }
    }

    const guestLast = await db.settings.get('last_opened_tab:guest');
    if (guestLast && !(await db.settings.get(`last_opened_tab:${uid}`))) {
      await db.settings.put({ key: `last_opened_tab:${uid}`, value: guestLast.value });
      await db.settings.delete('last_opened_tab:guest');
    }
  });
}

/**
 * F-011: Routine claim on sign-up:
 * When transitioning from guest session:
 * Check user's routines (from Dexie / server). If account has 0 routines, claim the device guest routine (ownerId = user.id);
 * if account already has >= 1 routine, delete/drop the guest demo routine so the user does not exceed the Free 1-routine cap.
 */
export async function claimOrDropGuestRoutine(uid: string): Promise<void> {
  try {
    let serverRoutinesCount = 0;
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      try {
        const { apiClient } = await import('../utils/apiClient.js');
        const serverRoutines = await apiClient.get<unknown[]>('/api/routines');
        if (Array.isArray(serverRoutines)) {
          serverRoutinesCount = serverRoutines.length;
        }
      } catch {
        // offline or error, fallback to local Dexie
      }
    }

    await db.transaction('rw', db.routines, async () => {
      const allRoutines = await db.routines.toArray();
      const userRoutinesInDexie = allRoutines.filter((r) => r.ownerId === uid && !r.deletedAt);
      const totalUserRoutines = serverRoutinesCount + userRoutinesInDexie.length;

      const guestRoutines = allRoutines.filter(
        (r) => (!r.ownerId || r.ownerId.startsWith('guest-')) && !r.deletedAt
      );

      if (totalUserRoutines >= 1) {
        // Account already has routine(s): delete guest demo routine(s) to avoid exceeding 1-routine cap
        for (const gr of guestRoutines) {
          await db.routines.delete(gr.id);
        }
      } else if (guestRoutines.length > 0) {
        // Account has 0 routines: claim the device guest routine (first one)
        const toClaim = guestRoutines[0];
        await db.routines.put({
          ...toClaim,
          ownerId: uid,
          isSample: 0,
          dirty: 1,
          updatedAt: new Date().toISOString()
        });
        // Delete any extra guest routines
        for (let i = 1; i < guestRoutines.length; i++) {
          await db.routines.delete(guestRoutines[i].id);
        }
      }
    });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('fretboard:routines-changed'));
    }
  } catch (err) {
    console.error('claimOrDropGuestRoutine failed:', err);
  }
}
